//-- Copyright (c) 2021 TMT Insights Group, LLC
import { selectJobTypes } from '@/redux/selectors/constantsSelectors';
import { ReactNode, useContext } from 'react';
import { ConfigContext } from '../../contextAPI/configContext';
import PolarisIcon from '../_common/PolarisIcon';
import TypeLabel, { typeLabelColorPresetKeys } from '../_common/TypeLabel';
import { useAppSelector } from '@/redux/hooks';

type JobTypeLabelProps = {
  type: string;
  style?: React.CSSProperties;
};

const iconDictionary: Record<string, ReactNode> = {
  movie: <PolarisIcon icon="movie" height={17} />,
  paperPlane: <PolarisIcon icon="paper_plane" height={14} />,
  computer: <PolarisIcon icon="computer" height={16} />,
  textDescription: <PolarisIcon icon="text" height={16} />,
  factCheck: <PolarisIcon icon="fact_check" height={17} />,
  audioWave: <PolarisIcon icon="audio" height={24} style={{ marginLeft: -5, marginRight: -5 }} />,
  folderReceived: <PolarisIcon icon="received" height={16} />,
  videoLibrary: <PolarisIcon icon="video_library" height={20} />,
  splitHint: <PolarisIcon icon="split_hint" height={19} />,
  closedCaption: <PolarisIcon icon="caption" height={18} />,
  default: <PolarisIcon icon="down_right" height={20} />
};

const iconPresets: Record<keyof typeof iconDictionary, string[]> = {
  movie: ['edit'],
  paperPlane: ['delivery'],
  computer: ['screening', 'compliance'],
  textDescription: ['localization'],
  factCheck: ['tech_eval', 'tech_review', 'qc'],
  audioWave: ['dubbing'],
  folderReceived: ['ingest', 'transform'],
  videoLibrary: ['proxy'],
  splitHint: ['segmentation'],
  closedCaption: ['subtitle']
};

const colorPresets: Record<string, string[]> = {
  orange: ['edit'],
  blue: ['delivery'],
  teal: ['screening', 'audio description', 'subtitle', 'compliance'],
  purple: ['tech_eval', 'tech_review', 'qc'],
  pink: ['dubbing'],
  green: ['ingest', 'transform'],
  lavender: ['segmentation', 'proxy'],
  gray: ['nothing']
};

const encodeStringToNumber = (str: string) => {
  let num = 0;
  for (let i = 0; i < str.length; i++) {
    num += str.charCodeAt(i);
  }
  return num;
};

function getNormalizedType(type: string) {
  let normalizedType = type.toLowerCase();
  if (normalizedType.includes('qc')) normalizedType = 'qc';
  if (normalizedType.includes('tech')) normalizedType = 'tech_review';
  if (normalizedType.includes('ingest')) normalizedType = 'ingest';
  if (normalizedType.includes('transform')) normalizedType = 'transform';
  if (normalizedType.includes('proxy')) normalizedType = 'proxy';
  if (normalizedType.includes('screening')) normalizedType = 'screening';
  if (normalizedType.includes('dubbing')) normalizedType = 'dubbing';
  if (normalizedType.includes('edit')) normalizedType = 'edit';
  if (normalizedType.includes('delivery')) normalizedType = 'delivery';
  if (normalizedType.includes('audio_desc')) normalizedType = 'dubbing';
  if (normalizedType.includes('segmentation')) normalizedType = 'segmentation';
  if (normalizedType.includes('subtitle')) normalizedType = 'subtitle';
  if (normalizedType.includes('ingest')) normalizedType = 'ingest';
  if (normalizedType.includes('compliance')) normalizedType = 'compliance';
  if (normalizedType.includes('nothing')) normalizedType = 'nothing';
  return normalizedType;
}

const getIcon = (type: string) => {
  let icon = iconDictionary.default;
  Object.keys(iconPresets).forEach((key) => {
    if (iconPresets[key].includes(type)) {
      icon = iconDictionary[key];
    }
  });
  return icon;
};

const getColor = (type: string) => {
  let color = null;
  Object.keys(colorPresets).forEach((key) => {
    if (colorPresets[key].includes(type)) {
      color = key;
    }
  });
  if (!color) {
    const num = encodeStringToNumber(type) % typeLabelColorPresetKeys.length;
    color = typeLabelColorPresetKeys[num];
  }
  return color;
};

const JobTypeLabel = ({ type = 'delivery', style }: JobTypeLabelProps) => {
  const jobTypeLabels = useAppSelector(selectJobTypes);
  const clientJobTypeLabelColor = useContext(ConfigContext)?.jobTypeLabelColor;
  const normalizedType = getNormalizedType(type);
  const icon = getIcon(normalizedType);
  const color = clientJobTypeLabelColor || getColor(normalizedType);

  return <TypeLabel label={jobTypeLabels[type] || normalizedType} icon={icon} color={color} style={style} />;
};

export default JobTypeLabel;
